import { useTranslation } from 'react-i18next';
import { ListOrdered, MoreVertical } from 'lucide-react';
import { PhotoOverlayButton } from './photo-editor/PhotoOverlayTools';
import { OVERLAY_ATTRIBUTE } from './board-grid/useZoomPan';

/** Making the climb a circuit, and the list that reads the circuit back. */
export interface CircuitModeControls {
  on: boolean;
  onToggle: (on: boolean) => void;
  /**
   * The list of holds, open over the wall. Left out where the circuit is read
   * back somewhere else — the gym panel's scans page gives it a column of its
   * own, so there is nothing over the picture to open.
   */
  panelOpen?: boolean;
  onTogglePanel?: (open: boolean) => void;
}

/**
 * Circuit mode, in the corner of the wall it is painted on.
 *
 * It belongs on the wall rather than in a row of controls above it: the order is
 * drawn on the holds, so what turns that order on — and what opens the list of it
 * — stands where the order is. One pair of buttons for every wall: a photo of a
 * boulder, a scan, a board's grid.
 */
export function CircuitOverlayButtons({ circuit }: { circuit: CircuitModeControls }) {
  const { t } = useTranslation();

  return (
    <>
      <PhotoOverlayButton
        label={t('climbEditor.circuit.toggle')}
        title={t('climbEditor.circuit.toggleHint')}
        pressed={circuit.on}
        onClick={() => circuit.onToggle(!circuit.on)}
      >
        <ListOrdered className="size-4" aria-hidden />
      </PhotoOverlayButton>

      {/* The way into the circuit itself — the order, and the hands that follow
          from it. Only once there is an order: on an ordinary climb the holds are
          a set, and a list of a set in no particular order says nothing. */}
      {circuit.on && circuit.onTogglePanel && (
        <PhotoOverlayButton
          label={t('climbEditor.circuit.more')}
          pressed={!!circuit.panelOpen}
          onClick={() => circuit.onTogglePanel?.(!circuit.panelOpen)}
        >
          <MoreVertical className="size-4" aria-hidden />
        </PhotoOverlayButton>
      )}
    </>
  );
}

/**
 * The same pair, in a corner of their own — for a wall with no other overlay to
 * sit beside. A photo of a boulder has the button that changes the picture there
 * and keeps them company itself (see `ClimbPhotoOverlayActions`).
 */
export function CircuitOverlayCorner({ circuit }: { circuit: CircuitModeControls }) {
  return (
    <div
      className="absolute left-2 top-2 flex items-center gap-1"
      // On the wall, not in it: the gesture that moved the picture has no claim on
      // this press, and the surface underneath hit-tests the rest.
      {...{ [OVERLAY_ATTRIBUTE]: '' }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <CircuitOverlayButtons circuit={circuit} />
    </div>
  );
}
